import { NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Optional } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RequestService } from '@df/api/request/request.service';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { IProductData, IProductDataV2, Product } from '@df/catalog/product/product';
import { ISizeName } from '@df/core/service/client';
import { Events } from '@df/core/service/event';
import { Status } from '@df/core/status';
import { MuiInputDirective } from '@df/legacy/ui/form/mui-input.directive';
import { ImageModule } from '@df/module-image/image.module';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ActionComponent } from '../../ui/api/action.component';
import { InputWrapBodyDirective } from '../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../ui/form/input-wrap.component';
import { LocalProduct } from '../../catalog/model/product/product.decorator';

export namespace BasketAddPromoGift {
    export const id = 'basket-add-prmo-gift-modal';
    export const EventAddGift = 'addGiftEvent';
    export interface IModalData {
        giftItems: BasketAddPromoGift.IGiftProduct[];
    }

    /**
     * Modal bLock data
     */
    export type IBlockData = Catalog.IBlockData<IBlockContent>;
    /**
     * Modal block content
     */
    export interface IBlockContent {
        title: string;
        subtitle?: string;
        addButtonCopy?: string;
        selectSizeButtonCopy?: string;
    }

    export interface IGiftProduct {
        parent: LocalProduct<IProductDataV2>;
        simpleProducts: LocalProduct<IProductDataV2>[];
        selectedProductId?: number | string;
    }

    export const GIFT_ENDPOINT = 'basket/addPromoItem';

    export interface IAddGiftRequestData {
        product_id: number;
    }
}

@DfCmsModal({
    id: BasketAddPromoGift.id,
    name: 'Basket Add PromoGift Popup',
    cmsNonOpenable: 'Modal can only be opened in a real case scenario',
    position: 'custom',
    config: (sizeId: ISizeName) => ({
        width: '100vw',
        maxWidth: '100vw',
        modalClass: 'p-a-0',
        minWidth: undefined
    }),
    reopenPolicy: NDfModal.ReopenPolicy.passData,
    sizeRestriction: (sizeId: ISizeName) => ['S', 'M', 'L', 'X']
})
@ICmsComponent<BasketAddPromoGiftComponent>({
    name: 'Basket Add Promo Gift Popup',
    id: BasketAddPromoGift.id,
    block: {
        url: BasketAddPromoGift.id
    },
    onDemandOnly: true,
    fields: [
        <NICmsContentField.IField>{
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            default: 'Pick One Free Gift',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'subtitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Subtitle',
            default: 'Please select an option below'
        },
        <NICmsContentField.IField>{
            key: 'addButtonCopy',
            type: NICmsContentField.EFieldType.text,
            title: 'Add Button Copy',
            default: 'Add free gift'
        },
        <NICmsContentField.IField>{
            key: 'selectSizeButtonCopy',
            type: NICmsContentField.EFieldType.text,
            title: 'Select Size Button Copy',
            default: 'Select an option'
        }
    ]
})
@Component({
    selector: 'basket-add-promo-gift',
    templateUrl: './basket-add-promo-gift.html',
    styleUrls: ['./basket-add-promo-gift.scss'],
    standalone: true,
    imports: [
        ActionComponent,
        FormsModule,
        ICmsClientModule,
        ImageModule,
        InputWrapComponent,
        InputWrapBodyDirective,
        MuiInputDirective,
        NgClass,
        NgFor,
        NgIf,
        OptionLabelPipe,
        SizeClassDirective,
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BasketAddPromoGiftComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: BasketAddPromoGift.IBlockContent;

    protected selectedParent?: number;
    protected giftProducts?: BasketAddPromoGift.IGiftProduct[];
    protected displayWarning?: boolean;
    protected confirmStatus = new Status();

    constructor(
        @Optional() private modalRef: ModalRef<BasketAddPromoGift.IBlockContent>,
        @Optional() @Inject(MODAL_DATA) data: BasketAddPromoGift.IModalData,
        private requestService: RequestService,
        private events: Events
    ) {
        super();
        if (data.giftItems.length) {
            this.giftProducts = data.giftItems;
        }
        this.detectChanges();
    }

    get selectedGiftProduct(): BasketAddPromoGift.IGiftProduct | undefined {
        return this.giftProducts?.find(p => p.parent.id === this.selectedParent);
    }

    protected selectParent(parentId: number): void {
        if (this.selectedParent !== parentId) {
            this.selectedParent = parentId;
            this.detectChanges();
        }
    }

    protected close() {
        this.modalRef.close();
        this.events.broadcast(BasketAddPromoGift.EventAddGift, undefined);
    }

    protected async confirm(): Promise<void> {
        if (!this.canConfirm) {
            if (!this.displayWarning) {
                this.displayWarning = true;
                this.detectChanges();
            }
            return;
        }

        await this.requestService.send(
            this.confirmStatus,
            {
                path: BasketAddPromoGift.GIFT_ENDPOINT,
                data: {
                    product_id: this.selectedGiftProduct?.selectedProductId
                }
            },
            data => {
                this.modalRef.close();
                this.events.broadcast(BasketAddPromoGift.EventAddGift, this.selectedGiftProduct?.selectedProductId);
            }
        );
    }

    get canConfirm(): boolean {
        return !!(
            this.selectedParent &&
            !!this.selectedGiftProduct?.selectedProductId &&
            this.selectedGiftProduct.selectedProductId !== 'undefined'
        );
    }
}
